"use client";

import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Home, ArrowLeft, RefreshCw, type LucideIcon } from "lucide-react";

interface ErrorPageLayoutProps {
  /** Error code to display prominently (e.g., "404", "500", "403") */
  code: string;
  /** Main error title */
  title: string;
  /** Descriptive message explaining the error */
  description: string;
  /** Icon to display (from lucide-react) */
  icon: LucideIcon;
  /** Whether to show a "Try Again" button (for recoverable errors) */
  showRetry?: boolean;
  /** Callback for retry action */
  onRetry?: () => void;
  /** Whether to show "Go Back" button */
  showGoBack?: boolean;
  /** Additional action buttons */
  additionalActions?: React.ReactNode;
  /** Label for the "Go Back" button */
  goBackLabel?: string;
  /** Label for the "Back to Home" button */
  backHomeLabel?: string;
  /** Label for the "Try Again" button */
  retryLabel?: string;
}

export function ErrorPageLayout({
  code,
  title,
  description,
  icon: Icon,
  showRetry = false,
  onRetry,
  showGoBack = true,
  additionalActions,
  goBackLabel = "Go Back",
  backHomeLabel = "Back to Home",
  retryLabel = "Try Again",
}: ErrorPageLayoutProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8 text-center md:p-12">
          {/* Error Code */}
          <div className="mb-6">
            <span className="text-8xl font-bold text-muted-foreground/20 md:text-9xl">{code}</span>
          </div>

          {/* Icon */}
          <div className="mb-6 flex justify-center">
            <div className="rounded-2xl border border-border bg-muted p-4">
              <Icon className="h-12 w-12 text-muted-foreground" />
            </div>
          </div>

          {/* Title */}
          <h1 className="mb-4 text-2xl font-semibold tracking-tight text-foreground">{title}</h1>

          {/* Description */}
          <p className="mb-8 text-base leading-relaxed text-muted-foreground md:text-lg">
            {description}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            {showGoBack && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => window.history.back()}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {goBackLabel}
              </Button>
            )}

            {showRetry && onRetry && (
              <Button variant="outline" size="lg" onClick={onRetry} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                {retryLabel}
              </Button>
            )}

            <Button asChild size="lg" className="gap-2">
              <Link to="/">
                <Home className="h-4 w-4" />
                {backHomeLabel}
              </Link>
            </Button>

            {additionalActions}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
